import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import DynamicTSLRanges from './DynamicTSLRanges';
import { brokerAPI, BrokerStatus, BrokerFeatureToggle } from '../services/brokerApi';
import {
  SpreadConfig,
  SpreadMarket,
  SpreadDirection,
  SpreadOptionType,
  SPREAD_LOT_SIZE,
  getLegOrder,
} from '../types/spread';

interface SpreadConfigFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (config: Partial<SpreadConfig>) => void;
  config?: SpreadConfig | null;
  market: SpreadMarket;
  direction: SpreadDirection;
  optionType: SpreadOptionType;
}

const FIRST_LEG_OFFSET_OPTIONS = [-3, -2, -1, 0, 1, 2, 3];
const SECOND_LEG_OFFSET_OPTIONS = [1, 2, 3, 4, 5];

const defaultFormData = (market: SpreadMarket, direction: SpreadDirection, optionType: SpreadOptionType): Partial<SpreadConfig> => ({
  market,
  direction,
  option_type: optionType,
  name: '',
  expiry_date: '',
  is_active: true,
  first_leg_offset: 1,
  second_leg_offset: 1,
  delay_seconds: 3,
  third_leg_enabled: false,
  x_value: 30,
  y_value: 10,
  capital_percentage: 1,
  slippage_percentage: 3,
  sl_x: 0,
  sl_y: 0,
  sl_points: 0,
  target_x: 0,
  target_y: 0,
  target_points: 0,
  tsl_points: 0,
  tsl_ranges: [{ entry_start: 30, entry_peak: 50, range: 10 }],
  broker: 'DHAN',
});

const SpreadConfigForm: React.FC<SpreadConfigFormProps> = ({
  isOpen, onClose, onSubmit, config, market, direction, optionType,
}) => {
  const [formData, setFormData] = useState<Partial<SpreadConfig>>(defaultFormData(market, direction, optionType));
  // Confirmed live: a double-click on "Create Config" (no disable-while-submitting guard existed)
  // created two identical configs — if both got Activated, that's two independent live entries
  // for the same setup. onSubmit is fire-and-forget from this component's side (the parent owns
  // the actual API call), so this is the only place that can block a second submit in time.
  const [submitting, setSubmitting] = useState(false);
  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  // NIFTY Spread and SENSEX Spread are independently toggleable per broker now, not one combined
  // 'SPREAD' switch — this form is already per-market (the `market` prop), so pick the matching one.
  const spreadFeature = market === 'SENSEX' ? 'SENSEX_SPREAD' : 'NIFTY_SPREAD';
  const isBrokerEnabledFor = (broker: string) =>
    featureToggles.find(t => t.broker === broker && t.feature === spreadFeature)?.is_enabled ?? (broker === 'DHAN');

  useEffect(() => {
    brokerAPI.list().then(res => setBrokers(res.data.filter(b => b.implemented))).catch(() => {});
    brokerAPI.listFeatures().then(res => setFeatureToggles(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFormData(config ? { ...config } : defaultFormData(market, direction, optionType));
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, config, market, direction, optionType]);

  if (!isOpen) return null;

  const legOrder = getLegOrder(direction, optionType);
  const lotSize = SPREAD_LOT_SIZE[market];
  const firstLegLabel = legOrder === 'BUY_FIRST' ? 'Buy' : 'Sell';
  const secondLegLabel = legOrder === 'BUY_FIRST' ? 'Sell' : 'Buy';
  const yFirst = direction === 'BEARISH';

  const firstOffsetValue = formData.first_leg_offset ?? 1;
  const firstLegTag = firstOffsetValue === 0 ? 'ATM' : `ATM${firstOffsetValue > 0 ? '+' : ''}${firstOffsetValue}`;
  // Tag convention for the leg placed second: letter comes from which leg was first
  // (B if Buy was first, S if Sell was first); sign comes from option type
  // (CE steps further OTM upwards = "+", PE steps further OTM downwards = "-").
  //   Bullish CE -> B+n   Bullish PE -> S-n
  //   Bearish CE -> S+n   Bearish PE -> B-n
  const secondLegLetter = legOrder === 'BUY_FIRST' ? 'B' : 'S';
  const secondLegSign = optionType === 'CE' ? '+' : '-';
  const secondLegTag = `${secondLegLetter}${secondLegSign}${formData.second_leg_offset ?? 1}`;

  // The optional split leg ("Offset-2") is always `second_leg_offset + 1`, tagged the same as
  // secondLegTag — confirmed live with concrete examples for BOTH directions:
  //   BUY_FIRST  (Bullish CE, Sell Offset B+3) -> split: SELL B+3 + B+4, BUY stays single.
  //   SELL_FIRST (Bullish PE, Buy Offset S-2)  -> split: BUY S-2 + S-3, SELL stays single.
  // i.e. it's always "split whichever leg sits at the second_leg_offset field" — never
  // "always split the SELL side" (that was tried and explicitly corrected by the client: a
  // SELL_FIRST config's split legs turned out to be BUY, not SELL, on the actual broker fills).
  const offset2Value = (formData.second_leg_offset ?? 1) + 1;
  const sellOffset2Tag = `${secondLegLetter}${secondLegSign}${offset2Value}`;

  const handleChange = (field: keyof SpreadConfig, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // already submitted once — ignore a second click/Enter before the modal closes
    if (!formData.name || !formData.expiry_date) return;
    setSubmitting(true);
    // Capital %/Delay allow a momentary '' while the user clears the field to retype (fixed: the
    // old `parseInt(e.target.value) || fallback` snapped the value back to the fallback on every
    // keystroke, including the empty one, so backspacing "1" to type "2" just re-rendered "1"
    // and the field was effectively stuck) — sanitize back to a valid number only here, at submit.
    const capitalPercentage = Number(formData.capital_percentage);
    const delaySeconds = Number(formData.delay_seconds);
    onSubmit({
      ...formData,
      capital_percentage: capitalPercentage > 0 ? capitalPercentage : 1,
      delay_seconds: delaySeconds >= 0 ? delaySeconds : 3,
    });
  };

  const sectionStyle = (borderColor: string, bg: string): React.CSSProperties => ({
    border: `1px solid ${borderColor}`,
    background: bg,
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '12px',
  });

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '4px' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{ background: 'white', borderRadius: '8px', width: '640px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 16px', borderBottom: '1px solid #e5e7eb',
          background: direction === 'BULLISH' ? '#ecfdf5' : '#fef2f2',
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: direction === 'BULLISH' ? '#065f46' : '#991b1b' }}>
              {market} SPREAD — {direction} ({optionType})
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              Execution order: <strong>{firstLegLabel} first → {secondLegLabel} after delay</strong>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
          {/* Name / Expiry / Buy / Sell offset / Delay */}
          <div style={sectionStyle('#e5e7eb', '#f9fafb')}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelStyle}>Config Name</label>
                <input style={inputStyle} value={formData.name || ''} onChange={e => handleChange('name', e.target.value)} required />
              </div>
              <div>
                <label style={labelStyle}>{market === 'NIFTY' ? 'Nifty' : 'Sensex'} Expiry Date</label>
                <input type="date" style={inputStyle} value={formData.expiry_date || ''} onChange={e => handleChange('expiry_date', e.target.value)} required />
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Broker</label>
              <select style={{ ...inputStyle, maxWidth: '220px' }} value={formData.broker || 'DHAN'} onChange={e => handleChange('broker', e.target.value)}>
                <option value="DHAN">Dhan</option>
                {brokers.filter(b => b.broker !== 'DHAN').map(b => (
                  <option key={b.broker} value={b.broker}>{b.broker.replace('_', ' ')}{!isBrokerEnabledFor(b.broker) ? ' (disabled)' : ''}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>{firstLegLabel} Offset (from ATM)</label>
                <select
                  style={inputStyle}
                  value={firstOffsetValue}
                  onChange={e => handleChange('first_leg_offset', parseInt(e.target.value))}
                >
                  {FIRST_LEG_OFFSET_OPTIONS.map(n => (
                    <option key={n} value={n}>{n === 0 ? 'ATM' : `ATM${n > 0 ? '+' : ''}${n}`}</option>
                  ))}
                </select>
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>Tag: {firstLegTag}</div>
              </div>
              <div>
                <label style={labelStyle}>{secondLegLabel} Offset (from {firstLegLabel} strike)</label>
                <select
                  style={inputStyle}
                  value={formData.second_leg_offset ?? 1}
                  onChange={e => handleChange('second_leg_offset', parseInt(e.target.value))}
                >
                  {SECOND_LEG_OFFSET_OPTIONS.map(n => (
                    <option key={n} value={n}>{secondLegLetter}{secondLegSign}{n}</option>
                  ))}
                </select>
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>Tag: {secondLegTag}</div>
              </div>
              <div>
                <label style={labelStyle}>Delay (seconds)</label>
                <input
                  type="number" min={0} style={{ ...inputStyle, borderColor: '#fca5a5' }}
                  value={formData.delay_seconds ?? 3}
                  onChange={e => handleChange('delay_seconds', e.target.value === '' ? '' : parseInt(e.target.value))}
                />
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                  Gap after {firstLegLabel.toLowerCase()} fills, before placing {secondLegLabel.toLowerCase()}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={!!formData.third_leg_enabled}
                  onChange={e => handleChange('third_leg_enabled', e.target.checked)}
                />
                Split {secondLegLabel} leg into two ({secondLegLabel} Offset + Offset-2)
              </label>
              {formData.third_leg_enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', maxWidth: '360px' }}>
                  <div>
                    <label style={labelStyle}>{secondLegLabel} Offset-2</label>
                    <select style={{ ...inputStyle, background: '#f3f4f6' }} value={sellOffset2Tag} disabled>
                      <option value={sellOffset2Tag}>{sellOffset2Tag}</option>
                    </select>
                    <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>Tag: {sellOffset2Tag} (always {secondLegTag} + 1 step further OTM)</div>
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', alignSelf: 'center' }}>
                    Lots split evenly between {secondLegTag} and {sellOffset2Tag} (e.g. 10 lots → 5 + 5).
                    {firstLegLabel} leg keeps the full lot count, unchanged.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ENTRY */}
          <div style={sectionStyle('#a7f3d0', '#ecfdf5')}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#065f46', margin: '0 0 12px 0' }}>ENTRY</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '70px 70px 90px 100px', gap: '12px' }}>
              {yFirst ? (
                <>
                  <div>
                    <label style={labelStyle}>Y</label>
                    <input type="number" style={inputStyle} value={formData.y_value ?? 0} onChange={e => handleChange('y_value', parseFloat(e.target.value))} />
                  </div>
                  <div>
                    <label style={labelStyle}>X</label>
                    <input type="number" style={inputStyle} value={formData.x_value ?? 0} onChange={e => handleChange('x_value', parseFloat(e.target.value))} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label style={labelStyle}>X</label>
                    <input type="number" style={inputStyle} value={formData.x_value ?? 0} onChange={e => handleChange('x_value', parseFloat(e.target.value))} />
                  </div>
                  <div>
                    <label style={labelStyle}>Y</label>
                    <input type="number" style={inputStyle} value={formData.y_value ?? 0} onChange={e => handleChange('y_value', parseFloat(e.target.value))} />
                  </div>
                </>
              )}
              <div>
                <label style={labelStyle}>% Cap</label>
                <input type="number" min={0.01} step={0.01} style={inputStyle} value={formData.capital_percentage ?? 1} onChange={e => handleChange('capital_percentage', e.target.value === '' ? '' : parseFloat(e.target.value))} />
              </div>
              <div>
                <label style={labelStyle}>Slippage (%)</label>
                <input type="number" step="0.01" style={inputStyle} value={formData.slippage_percentage ?? 0} onChange={e => handleChange('slippage_percentage', parseFloat(e.target.value))} />
              </div>
            </div>
            <div style={{ fontSize: '10px', color: '#065f46', marginTop: '8px' }}>
              Quantity = (% Cap of configured Capital Amount) ÷ SELL leg's LTP, rounded down to a whole number of lots ({market} lot size {lotSize})
            </div>
            <div style={{ fontSize: '10px', color: direction === 'BULLISH' ? '#065f46' : '#991b1b', marginTop: '2px' }}>
              Entry triggers on {direction === 'BULLISH' ? 'X count' : 'Y count'} (same as existing NSE/BSE X/Y logic)
            </div>
          </div>

          {/* SL & TGT side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={sectionStyle('#fecaca', '#fef2f2')}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b', margin: '0 0 12px 0' }}>SL</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {yFirst ? (
                  <>
                    <div><label style={labelStyle}>Y</label><input type="number" style={inputStyle} value={formData.sl_y ?? 0} onChange={e => handleChange('sl_y', parseFloat(e.target.value))} /></div>
                    <div><label style={labelStyle}>X</label><input type="number" style={inputStyle} value={formData.sl_x ?? 0} onChange={e => handleChange('sl_x', parseFloat(e.target.value))} /></div>
                  </>
                ) : (
                  <>
                    <div><label style={labelStyle}>X</label><input type="number" style={inputStyle} value={formData.sl_x ?? 0} onChange={e => handleChange('sl_x', parseFloat(e.target.value))} /></div>
                    <div><label style={labelStyle}>Y</label><input type="number" style={inputStyle} value={formData.sl_y ?? 0} onChange={e => handleChange('sl_y', parseFloat(e.target.value))} /></div>
                  </>
                )}
                <div><label style={labelStyle}>NP</label><input type="number" style={inputStyle} value={formData.sl_points ?? 0} onChange={e => handleChange('sl_points', parseFloat(e.target.value))} /></div>
              </div>
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>NP = {market} Points based SL</div>
            </div>

            <div style={sectionStyle('#a7f3d0', '#ecfdf5')}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#065f46', margin: '0 0 12px 0' }}>TGT</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {yFirst ? (
                  <>
                    <div><label style={labelStyle}>Y</label><input type="number" style={inputStyle} value={formData.target_y ?? 0} onChange={e => handleChange('target_y', parseFloat(e.target.value))} /></div>
                    <div><label style={labelStyle}>X</label><input type="number" style={inputStyle} value={formData.target_x ?? 0} onChange={e => handleChange('target_x', parseFloat(e.target.value))} /></div>
                  </>
                ) : (
                  <>
                    <div><label style={labelStyle}>X</label><input type="number" style={inputStyle} value={formData.target_x ?? 0} onChange={e => handleChange('target_x', parseFloat(e.target.value))} /></div>
                    <div><label style={labelStyle}>Y</label><input type="number" style={inputStyle} value={formData.target_y ?? 0} onChange={e => handleChange('target_y', parseFloat(e.target.value))} /></div>
                  </>
                )}
                <div><label style={labelStyle}>NP</label><input type="number" style={inputStyle} value={formData.target_points ?? 0} onChange={e => handleChange('target_points', parseFloat(e.target.value))} /></div>
              </div>
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>NP = {market} Points based Target</div>
            </div>
          </div>

          {/* TSL */}
          <div style={sectionStyle('#bfdbfe', '#eff6ff')}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a', margin: '0 0 12px 0' }}>TSL</h3>
            <div style={{ marginBottom: '10px', width: '120px' }}>
              <label style={labelStyle}>NP (base)</label>
              <input type="number" style={inputStyle} value={formData.tsl_points ?? 0} onChange={e => handleChange('tsl_points', parseFloat(e.target.value))} />
            </div>
            <DynamicTSLRanges
              xValue={formData.x_value || 0}
              tslRanges={formData.tsl_ranges || []}
              onChange={ranges => handleChange('tsl_ranges', ranges)}
            />
          </div>

          {/* Exit order note */}
          <div style={{ fontSize: '11px', color: '#6b7280', padding: '8px 10px', background: '#f3f4f6', borderRadius: '6px', marginBottom: '14px' }}>
            <strong>Exit order:</strong> {secondLegLabel} first, then {firstLegLabel} — always the reverse of the entry sequence.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', fontSize: '13px' }}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: submitting ? '#9ca3af' : (direction === 'BULLISH' ? '#10b981' : '#ef4444'), color: 'white', fontSize: '13px', fontWeight: 600, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving...' : (config ? 'Update Config' : 'Create Config')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpreadConfigForm;
